import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useInterval } from './useInterval';

const millisecondsPerSecond = 1000;
const animationInterval = 0.6 * millisecondsPerSecond;

export const useMapAnimation = ({ shouldAnimate, holdFrames, frameKeys, priorityFrameIndex, onFrameChange }) => {
  const frameKeyLoaded = useRef({});

  // A counter that is mod with the number of frames to set our currentFrameIndex.
  const animationCounter = useRef(0);

  // The frame that would be used if we are animating.  Overridden by debugFrameNumberOverride or shouldAnimate=false.
  const shownFrameIndex = useRef(priorityFrameIndex);

  /** Functions **/

  const currentFrameIndex = () => shownFrameIndex.current;

  const totalFrames = frameKeys == null ? 0 : frameKeys.length;

  const frameKeyIsLoaded = key => frameKeyLoaded.current[key];

  const frameKeyDidFinishLoading = key => {
    frameKeyLoaded.current[key] = true;
  };

  const frameKeyAtIndex = index => {
    if (index != null && frameKeys != null && index >= 0 && frameKeys.length > index) {
      return frameKeys[index];
    }

    return null;
  };

  // Returns the number of frames that haven't finished loading yet.
  const framesMissing = () => {
    let missingCount = 0;

    for (let frameIndex = 0; frameIndex < frameKeys?.length ?? 0; frameIndex++) {
      const frameKey = frameKeyAtIndex(frameIndex);

      missingCount += frameKeyIsLoaded(frameKey) ? 1 : 0;
    }

    return missingCount;
  };

  const reset = () => {
    frameKeyLoaded.current = {};
  };

  /** Hooks **/

  // Update our animation counter.
  useInterval(() => {
    if (!shouldAnimate) {
      return;
    }

    const previousAnimationCounter = animationCounter.current ?? 0;

    animationCounter.current = previousAnimationCounter + 1;

    const animationTotalFrames = frameKeys == null ? 0 : frameKeys.length;

    const nextAnimationFrame = Math.min(
      (previousAnimationCounter + 1) % (animationTotalFrames + holdFrames),
      animationTotalFrames - 1, // nextAnimationFrame could be a hold frame, so use the last animation frame.
    );

    if (nextAnimationFrame !== shownFrameIndex.current) {
      shownFrameIndex.current = nextAnimationFrame;

      if (onFrameChange !== undefined) {
        onFrameChange(nextAnimationFrame);
      }
    }
  }, animationInterval);

  useEffect(() => {
    if (!shouldAnimate) {
      shownFrameIndex.current = priorityFrameIndex;

      if (onFrameChange !== undefined) {
        onFrameChange(shownFrameIndex.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityFrameIndex]);

  // Check if there are any values in frameKeyLoaded for keys that aren't in frameKeys.  If so, remove them.
  for (const key of Object.keys(frameKeyLoaded.current)) {
    if (!frameKeys.includes(key)) {
      delete frameKeyLoaded.current[key];
    }
  }

  return {
    currentFrameIndex,
    totalFrames,
    frameKeyAtIndex,
    frameKeyIsLoaded,
    frameKeyDidFinishLoading,
    framesMissing,
    reset,
  };
};

useMapAnimation.propTypes = {
  shouldAnimate: PropTypes.bool,
  priorityFrameIndex: PropTypes.number, // The frame shown by default if we aren't animating.
  holdFrames: PropTypes.number, // The number of frames to hold at the end of an animation.
  frameKeys: PropTypes.array,
  onFrameChange: PropTypes.func, // Called when the frame number that should be displayed changes.
};

useMapAnimation.defaultProps = {
  shouldAnimate: false,
  priorityFrameIndex: null,
  holdFrames: null,
  frameKeys: null,
  onFrameChange: () => {},
};
